import { Component, OnInit, OnDestroy, ViewChild, ElementRef, AfterViewInit, inject, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';

@Component({
    selector: 'about-widget',
    standalone: true,
    imports: [CommonModule],
    template: `
        <section id="about" class=" overflow-visible mt-4">

            <!-- Imagen mobile: full-width al corte -->
            <div class="block md:hidden w-full">
                <img src="/assets/img/home/LetsDoThisMobile-01.png"
                    alt="Let's do this - Usina Cowork Buenos Aires"
                    loading="lazy"
                    class="w-full block pointer-events-none" />
            </div>
            
            <div class="hidden md:flex w-full relative overflow-visible marquee-container">
                @for (img of bgImages; track img; let i = $index) {
                    <img [src]="img" 
                         alt="Usina Cowork Background" 
                         class="absolute top-0 left-0 w-full h-full object-cover z-0 transition-opacity duration-1000"
                         [style.opacity]="i === currentImgIndex ? '1' : '0'" />
                }
                
               <!-- Imagen desktop: overlay con posicionado original -->
               <div class="absolute z-10 justify-content-center lets-build-img"
                    style="left: 43%; transform: translateX(-50%);">
                   <img src="/assets/img/home/letsbuildthis-01.png"
                       alt="Let's build this community - Usina Cowork"
                       loading="lazy"
                       class="w-full h-full object-contain pointer-events-none"
                       style="filter: drop-shadow(0px 15px 25px rgba(0,0,0,0.1));" />
               </div>
            </div>

            <div class="grid grid-nogutter relative z-2 about-text-section">
                
                <div class="hidden lg:block lg:col-6 relative mb-6 lg:mb-0">
                    
                    <div class="relative overflow-hidden" style="aspect-ratio: 4/3;">
                        
                        <img src="/assets/img/home/about.jpg" 
                             alt="Espacio de trabajo colaborativo en Usina Cowork Chacarita" 
                             loading="lazy"
                             class="absolute top-0 left-0 w-full h-full object-cover block transition-all duration-500"
                             [style.opacity]="currentTextIndex === 0 ? '1' : '0'"
                             [style.pointer-events]="currentTextIndex === 0 ? 'auto' : 'none'" />

                        <video #videoPlayerAbout autoplay loop muted playsinline 
                               class="absolute top-0 left-0 w-full h-full object-cover block transition-all duration-500"
                               poster="/assets/img/home/about.jpg"
                               [style.opacity]="currentTextIndex === 1 ? '1' : '0'"
                               [style.pointer-events]="currentTextIndex === 1 ? 'auto' : 'none'">
                            <source src="/assets/video/about.mp4" type="video/mp4">
                        </video>

                    </div>
                    <div class="gradient-line mb-4 w-full"></div>
                </div>

                <div class="col-12 lg:col-6 pl-4 md:pl-8 pt-4 flex flex-column justify-content-center"
                     (touchstart)="onTouchStart($event)"
                     (touchend)="onTouchEnd($event)">
                        
                        <div class="fade-text">
                            <div class="mb-4">
                                <h2 class="font-heavy text-4xl md:text-6xl mb-4 line-height-1">
                                    {{ textBlocks[currentTextIndex].title }}
                                </h2>
                                <h3 class="font-medium text-3xl md:text-5xl mb-4 md:line-height-2">
                                    {{ textBlocks[currentTextIndex].subtitle }}
                                </h3>
                            </div>

                            <div class="font-regular text-lg text-800 line-height-2 w-10 pt-4" style="min-height: 250px;">
                                @for(paragraph of textBlocks[currentTextIndex].paragraphs; track paragraph) {
                                    <p class="mb-3">{{ paragraph }}</p>
                                }
                                
                                @if(textBlocks[currentTextIndex].quote) {
                                    <p class="font-regular text-900 mt-2 uppercase tracking-wide">
                                        {{ textBlocks[currentTextIndex].quote }}
                                    </p>
                                }
                            </div>
                        </div>

                        <div class="flex align-items-center gap-3 mt-5">
                            @for(block of textBlocks; track block.title; let i = $index) {
                                <div class="border-circle cursor-pointer transition-all duration-300"
                                    style="width: 0.6rem; height: 0.6rem;"
                                    [ngClass]="i === currentTextIndex ? 'bg-black-alpha-90 scale-110' : 'bg-black-alpha-30 hover:bg-black-alpha-50'"
                                    (click)="setManualText(i)"
                                    [title]="'Ver texto ' + (i + 1)">
                                </div>
                            }
                        </div>

                </div>
            </div>
        </section>
    `,
    styles: [`
        .font-heavy { font-family: "ABCDiatype-Heavy", sans-serif; }
        .font-medium { font-family: "ABCDiatype-Medium", sans-serif; }
        .font-regular { font-family: "ABCDiatype-Regular", sans-serif; }

        .marquee-container { aspect-ratio: 21 / 9; overflow: visible !important; }
        @media (max-width: 1024px) { .marquee-container { aspect-ratio: 16 / 10; } }
        @media (max-width: 768px) { .marquee-container { aspect-ratio: 4 / 3; } }

        .gradient-line {
            background: linear-gradient(90deg, #00ff00 0%, #00ff00 100%);
            height: 4px;
        }

        img { display: block; }

        .lets-build-img { width: 125%; height: 142%; top: -14%; }
        @media (max-width: 768px) {
            .lets-build-img { width: 100% !important; height: 100% !important; top: 19% !important; }
        }

        .fade-text { animation: fadeIn 0.8s ease-in-out; }
        @keyframes fadeIn {
            from { opacity: 0; transform: translateY(5px); }
            to { opacity: 1; transform: translateY(0); }
        }
        .scale-110 { transform: scale(1.2); }

        /* =============================================
           ABOUT TEXT SECTION – desktop & mobile
           ============================================= */
        .about-text-section {
            padding: 11rem 0 7rem 0;
            background: linear-gradient(to bottom, rgba(0, 255, 0, 0.9) 0%, var(--usina-bg, #F2F2F2) 40%);
        }
        @media (max-width: 768px) {
            .about-text-section {
                /* Sin gradiente neon en mobile: la imagen ya da el contexto visual */
                background: transparent;
                /* Padding reducido: el marquee no existe en mobile */
                padding: 2rem 0 4rem 0;
            }
        }
    `]
})
export class AboutWidget implements OnInit, OnDestroy, AfterViewInit {

    @ViewChild('videoPlayerAbout') videoElement!: ElementRef<HTMLVideoElement>
    private platformId = inject(PLATFORM_ID);

    ngAfterViewInit(): void {
        if (!isPlatformBrowser(this.platformId)) return;
        if (this.videoElement && this.videoElement.nativeElement) {
            const video = this.videoElement.nativeElement;

            // Reaseguramos el muted (requisito indispensable para autoplay)
            video.muted = true;
            
            // --- REDUCCIÓN DE VELOCIDAD DEL VIDEO ---
            // 0.6 significa que se reproducirá al 60% de su velocidad normal.
            // Si quieres que sea aún más lento prueba con 0.5.
            video.playbackRate = 0.6; 

            const playPromise = video.play();

            if (playPromise !== undefined) {
                playPromise.then(_ => {
                    // Autoplay comenzó correctamente
                }).catch(error => {
                    console.warn("Autoplay prevenido por el navegador, reintentando muted:", error);
                    video.muted = true;
                    video.play();
                });
            }
        }
    }
    
    // --- LÓGICA DE IMÁGENES ---
    bgImages: string[] = [
        '/assets/img/carrusel_about/01.jpg',
        '/assets/img/carrusel_about/02.jpg',
        '/assets/img/carrusel_about/03.jpg',
        '/assets/img/carrusel_about/04.jpg',
        '/assets/img/carrusel_about/05.jpg',
        '/assets/img/carrusel_about/06.jpg',
        '/assets/img/carrusel_about/07.jpg',
        '/assets/img/carrusel_about/08.jpg',
        '/assets/img/carrusel_about/09.jpg',
        '/assets/img/carrusel_about/10.jpg',
        '/assets/img/carrusel_about/11.jpg',
        '/assets/img/carrusel_about/12.jpg',
        '/assets/img/carrusel_about/13.jpg',
    ];
    currentImgIndex: number = 0;
    private imgIntervalId: any;

    // --- LÓGICA DE TEXTOS ---
    textBlocks = [
        {
            title: 'Our community, our friends.',
            subtitle: 'Un espacio de trabajo, un venue cultural comprometido con el apoyo al emprendimiento, la innovación y la comunidad.',
            paragraphs: [
                'Un coworking es el resultado inmediato de una inteligencia colectiva.',
                'Muy propia de las logicas digitales y el razonamiento de red donde lo “comun” se vuelve mas atractivo que “lo propio” y la idea de que en lo compartido hay un valor mas profundo que en lo individual.',
                'Quien busca la conexión, encuentra influencia.',
                'Usina es un espacio innovador que comparte la convivencia, la experiencia, la naturaleza y la disrupción con personas y soluciones de todo el mundo.',
                'Nuestros CoWorkers comparten recursos e intercambian conocimientos, y juntos, pueden crear soluciones impensables e impacto positivo con solo compartir experiencias y pensamientos.'
            ],
            quote: 'We are doing this for you, we are doing this for us.'
        },
        {
            title: 'What are you building?',
            subtitle: 'Usina es una historia que muestra cómo la comunidad puede ayudarte a crecer y convertirte en una mejor versión de vos mismo.',
            paragraphs: [
                'El origen de Usina fue crear un espacio de trabajo tan creativo como vos.',
                'Usina es un espacio de coworking y un centro cultural con una misión simple: impulsar la creatividad y el espíritu emprendedor.',
                'Nuestro objetivo es que, durante tu tiempo en nuestras sedes, tu mente fluya de forma natural. Esto se debe a que está comprobado que la naturaleza fomenta la calma, la creatividad y la concentración. Por eso, diseñamos nuestros espacios para albergar una diversidad de industrias, personas y tipos de negocios.',
                'Somos un oasis en medio de la ciudad, donde podrás disfrutar de áreas verdes, luz natural en todas las oficinas y una comunidad diversa y enriquecedora, basada en el principio de que las mejores innovaciones surgen del encuentro entre las mentes más brillantes de diferentes industrias y disciplinas.'
            ],
            quote: '' 
        }
    ];
    currentTextIndex: number = 0;
    private textIntervalId: any;

    ngOnInit() {
        if (!isPlatformBrowser(this.platformId)) return;
        this.imgIntervalId = setInterval(() => {
            this.currentImgIndex = (this.currentImgIndex + 1) % this.bgImages.length;
        }, 3000);
    }

    ngOnDestroy() {
        if (this.imgIntervalId) clearInterval(this.imgIntervalId);
        this.stopTextSlider();
    }

    startTextSlider() {
        this.textIntervalId = setInterval(() => {
            this.currentTextIndex = (this.currentTextIndex + 1) % this.textBlocks.length;
            this.triggerTextAnimation();
        }, 8000); 
    }

    stopTextSlider() {
        if (this.textIntervalId) {
            clearInterval(this.textIntervalId);
        }
    }

    setManualText(index: number) {
        if (this.currentTextIndex !== index) {
            this.currentTextIndex = index;
            this.triggerTextAnimation();
            
            this.stopTextSlider();
            // this.startTextSlider(); // Si reactivas el autoplay, reactiva esto también
        }
    }

    triggerTextAnimation() {
        if (!isPlatformBrowser(this.platformId)) return;
        const element = document.querySelector('.fade-text') as HTMLElement;
        if (element) {
            element.style.animation = 'none';
            element.offsetHeight;
            element.style.animation = 'fadeIn 0.8s ease-in-out';
        }
    }

    // --- SWIPE TOUCH (mobile) ---
    private touchStartX: number = 0;

    onTouchStart(event: TouchEvent): void {
        this.touchStartX = event.changedTouches[0].clientX;
    }

    onTouchEnd(event: TouchEvent): void {
        const deltaX = event.changedTouches[0].clientX - this.touchStartX;
        const threshold = 40; // px mínimos para considerar swipe

        if (Math.abs(deltaX) < threshold) return; // movimiento muy corto, ignorar

        const total = this.textBlocks.length;
        if (deltaX < 0) {
            // Swipe izquierda → siguiente bloque
            this.setManualText((this.currentTextIndex + 1) % total);
        } else {
            // Swipe derecha → bloque anterior
            this.setManualText((this.currentTextIndex - 1 + total) % total);
        }
    }
}